/**
 * 性能优化工具类
 * 提供函数防抖（debounce）与节流（throttle）功能
 */
class OptimizationUtils {
    /**
     * 函数防抖：在停止触发后等待指定时间才执行
     * @param {Function} fn - 需要防抖的函数
     * @param {number} wait - 等待时间（毫秒），默认 300ms
     * @param {Object} options - 配置项
     * @param {boolean} [options.leading=false] - 是否在开始时触发
     * @param {boolean} [options.trailing=true] - 是否在结束时触发
     * @param {number|null} [options.maxWait=null] - 最长等待时间（毫秒），超时后强制执行
     * @returns {Function} 包装后的防抖函数，带有 cancel 与 flush 方法
     */
    static debounce(fn, wait = 300, options = {}) {
        if (typeof fn !== 'function') {
            throw new TypeError('debounce 需要传入一个函数');
        }

        const leading = !!options.leading;
        const trailing = options.trailing !== false; // 默认为 true
        const hasMaxWait = typeof options.maxWait === 'number' && options.maxWait >= 0;
        const maxWait = hasMaxWait ? Math.max(options.maxWait, wait) : null;

        // 定时器句柄与最近一次调用/执行的上下文与参数
        let timerId = null;
        let lastArgs;
        let lastThis;
        let lastCallTime = 0; // 最近一次被调用的时间戳
        let lastInvokeTime = 0; // 最近一次实际执行原函数的时间戳
        let result; // 最近一次执行原函数返回值

        // 执行原始函数并更新时间标记，随后清理临时引用
        function invokeFunc(time) {
            lastInvokeTime = time;
            result = fn.apply(lastThis, lastArgs);
            lastArgs = lastThis = undefined;
            return result;
        }

        // 启动/重置计时器
        function startTimer(pendingFunc, ms) {
            timerId = setTimeout(pendingFunc, ms);
        }

        // 计算剩余等待时长（结合 maxWait 与常规 wait）
        function remainingWait(time) {
            const timeSinceLastCall = time - lastCallTime;
            const timeSinceLastInvoke = time - lastInvokeTime;
            const timeWaiting = wait - timeSinceLastCall;
            return hasMaxWait ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
        }

        // 是否应当在当前时刻触发执行
        function shouldInvoke(time) {
            const timeSinceLastCall = time - lastCallTime;
            const timeSinceLastInvoke = time - lastInvokeTime;

            return lastCallTime === 0 ||
                timeSinceLastCall >= wait ||
                timeSinceLastCall < 0 ||
                (hasMaxWait && timeSinceLastInvoke >= maxWait);
        }

        // 计时器到期后的收尾逻辑：按需触发 trailing 调用
        function trailingEdge(time) {
            timerId = null;
            if (trailing && lastArgs) {
                return invokeFunc(time);
            }
            lastArgs = lastThis = undefined;
            return result;
        }

        // 定时器回调：若应当执行则进入收尾，否则基于剩余时间继续等待
        function timerExpired() {
            const time = Date.now();
            if (shouldInvoke(time)) {
                return trailingEdge(time);
            }
            startTimer(timerExpired, remainingWait(time));
        }

        // 触发边缘：可在 leading 下立即执行一次，并开启计时器以等待 trailing
        function leadingEdge(time) {
            // 记录最后执行时间
            lastInvokeTime = time;
            // 如果需要立即执行
            if (leading) {
                result = invokeFunc(time);
            }
            // 启动定时器，等待 trailing 调用
            startTimer(timerExpired, wait);
            return result;
        }

        // 取消未完成的定时执行
        function cancel() {
            if (timerId !== null) {
                clearTimeout(timerId);
            }
            lastInvokeTime = 0;
            lastCallTime = 0;
            lastArgs = lastThis = undefined;
            timerId = null;
        }

        // 立即执行尾随（若存在待执行的调用）并返回结果
        function flush() {
            if (timerId === null) return result;
            const time = Date.now();
            return trailingEdge(time);
        }

        // 返回的包装函数：处理每次被调用时的节奏控制
        function debounced() {
            const time = Date.now();
            const isInvoking = shouldInvoke(time);

            lastArgs = arguments;
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            lastThis = this;
            lastCallTime = time;

            if (isInvoking) {
                if (timerId === null) {
                    return leadingEdge(time);
                }
                if (hasMaxWait) {
                    // 强制在 maxWait 之后执行一次
                    clearTimeout(timerId);
                    startTimer(timerExpired, remainingWait(time));
                    return invokeFunc(time);
                }
            }

            if (timerId === null) {
                startTimer(timerExpired, wait);
            }

            return result;
        }

        debounced.cancel = cancel;
        debounced.flush = flush;

        return debounced;
    }

    /**
     * 函数节流：保证在指定时间内最多只执行一次
     * @param {Function} fn - 需要节流的函数
     * @param {number} wait - 时间窗口（毫秒），默认 300ms
     * @param {Object} options - 配置项
     * @param {boolean} [options.leading=true] - 是否在开始时触发
     * @param {boolean} [options.trailing=true] - 是否在结束时再触发一次
     * @returns {Function} 包装后的节流函数，带有 cancel 与 flush 方法
     */
    static throttle(fn, wait = 300, options = {}) {
        const leading = options.leading !== false; // 默认为 true
        const trailing = options.trailing !== false; // 默认为 true
        // 通过设置 maxWait = wait，复用 debounce 实现“时间窗口内至少执行一次”的节流语义
        return OptimizationUtils.debounce(fn, wait, { leading, trailing, maxWait: wait });
    }
}

// 导出模块（UMD）
if (typeof module !== 'undefined' && module.exports) {
    module.exports = OptimizationUtils;
} else if (typeof window !== 'undefined') {
    window.OptimizationUtils = OptimizationUtils;
}

// 使用示例
// 防抖：窗口大小变化结束 300ms 后再计算布局
const onResize = OptimizationUtils.debounce(() => {
    // console.log('resize 结束，重新计算布局');
}, 300);

// 节流：滚动时每 200ms 计算一次位置
const onScroll = OptimizationUtils.throttle(() => {
    // console.log('节流中的滚动处理');
}, 200);

// 如果在浏览器中测试：
if (typeof window !== 'undefined') {
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });
}
